import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { useGuestAppointment } from '@/hooks/use-public-schedule';
import { useAuthStore } from '@/store/authStore';
import { APPOINTMENT_STATUS } from '@/types/appointment';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { useIconChip } from '@/hooks/use-theme-colors';

const DEFAULT_BRAND = '#FD5001';
const REDIRECT_SECONDS = 8;

/**
 * Confirmação do agendamento do convidado (B2): rota própria — sobrevive a
 * refresh e ao botão voltar, ao contrário do bloco inline que existia na
 * página do serviço. Leva ao agendamento (a "home" de quem não tem conta) e
 * oferece a conta pelo caminho.
 */
export default function GuestBookingSuccessScreen() {
  const chip = useIconChip();
  const { appointmentId, token, slug, brand } = useLocalSearchParams<{
    appointmentId: string;
    token: string;
    slug?: string;
    brand?: string;
  }>();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const brandColor = typeof brand === 'string' && brand ? brand : DEFAULT_BRAND;
  const magicToken = typeof token === 'string' ? token : undefined;

  const { data: appointment, isLoading } = useGuestAppointment(appointmentId, magicToken);

  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const [autoRedirect, setAutoRedirect] = useState(true);
  const appointmentPath = useRef({
    pathname: '/agendamento/[appointmentId]' as const,
    params: { appointmentId, token: magicToken ?? '' },
  });

  useEffect(() => {
    if (!autoRedirect) return;
    if (secondsLeft <= 0) {
      router.replace(appointmentPath.current as never);
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [autoRedirect, secondsLeft]);

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-neutral-50">
        <ActivityIndicator size="large" color={brandColor} />
      </View>
    );
  }

  const pending = appointment?.status === APPOINTMENT_STATUS.PENDING;

  return (
    <View className="flex-1 bg-neutral-50">
      <PublicBrandHeader />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1 bg-neutral-50"
        contentContainerStyle={{ flexGrow: 1, padding: 20, gap: 20 }}
      >
        <View
          className="items-center gap-3 rounded-3xl bg-card p-6"
          style={{ borderCurve: 'continuous' }}
        >
          <View
            className="h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: chip(brandColor, '1A').bg }}
          >
            <Ionicons
              name={pending ? 'hourglass-outline' : 'checkmark-circle'}
              size={34}
              color={chip(brandColor, '1A').fg}
            />
          </View>
          <ThemedText className="text-center font-bold text-2xl">
            {pending ? 'Solicitação enviada!' : 'Horário confirmado! 🎉'}
          </ThemedText>
          {!!appointment && (
            <>
              <ThemedText className="text-center font-semibold text-base text-neutral-700">
                {appointment.serviceName}
              </ThemedText>
              <ThemedText className="text-center text-base text-neutral-600">
                {formatAppointmentWhen(appointment.startAt)}
              </ThemedText>
              {!!appointment.address && (
                <View className="flex-row items-center gap-1.5">
                  <Ionicons className="text-neutral-500" name="location-outline" size={15} />
                  <ThemedText className="text-sm text-neutral-500">
                    {appointment.address}
                  </ThemedText>
                </View>
              )}
            </>
          )}
          <ThemedText className="text-center text-sm text-neutral-500">
            {pending
              ? 'Você receberá a confirmação do prestador pelo WhatsApp.'
              : 'Enviamos os detalhes e o link do agendamento pelo WhatsApp.'}
          </ThemedText>
        </View>

        {!isAuthenticated && (
          <View className="gap-3 rounded-3xl bg-card p-6" style={{ borderCurve: 'continuous' }}>
            <View className="flex-row items-center gap-3">
              <View
                className="h-10 w-10 items-center justify-center rounded-2xl"
                style={{ backgroundColor: chip(brandColor, '1A').bg, borderCurve: 'continuous' }}
              >
                <Ionicons name="sparkles-outline" size={20} color={chip(brandColor, '1A').fg} />
              </View>
              <ThemedText className="flex-1 font-bold text-lg">Guarde seus agendamentos</ThemedText>
            </View>
            <ThemedText className="text-sm leading-5 text-neutral-500">
              Seus dados já estão salvos. É só confirmar seu WhatsApp para remarcar, ver o histórico
              e agendar em um toque na próxima vez.
            </ThemedText>
            <PressableScale
              haptic
              onPress={() => {
                setAutoRedirect(false);
                router.push({
                  pathname: '/agendamento/[appointmentId]/conta',
                  params: {
                    appointmentId,
                    token: magicToken ?? '',
                    brand: brandColor,
                    ...(slug ? { slug } : {}),
                  },
                } as never);
              }}
              className="mt-1 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
              style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
            >
              <ThemedText className="font-bold text-base text-white">Criar minha conta</ThemedText>
            </PressableScale>
          </View>
        )}

        <View className="gap-2">
          <PressableScale
            onPress={() => {
              setAutoRedirect(false);
              router.replace(appointmentPath.current as never);
            }}
            className="w-full items-center rounded-full border border-neutral-200 bg-card py-4"
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText className="font-bold text-neutral-700">Ver meu agendamento</ThemedText>
          </PressableScale>

          {!!slug && (
            <Pressable
              onPress={() => {
                setAutoRedirect(false);
                router.replace(`/@${slug}` as never);
              }}
              className="items-center py-2"
            >
              <ThemedText className="font-semibold text-sm text-neutral-500">
                Voltar ao perfil
              </ThemedText>
            </Pressable>
          )}

          {autoRedirect && (
            <Pressable onPress={() => setAutoRedirect(false)} className="items-center py-1">
              <ThemedText className="text-xs text-neutral-400">
                Abrindo seu agendamento em {secondsLeft}s · toque para ficar aqui
              </ThemedText>
            </Pressable>
          )}
        </View>

        <View className="flex-1" />
        <View style={{ marginHorizontal: -20, marginTop: 24 }}>
          <PublicBrandFooter />
        </View>
      </ScrollView>
    </View>
  );
}
